import {
  ConditionOperator,
  LogicCondition,
  LogicIteration,
} from "@easy-email-pro-core/typings";
import { TemplateEnginePlugin } from "./PluginManager";

import { Raw } from "@easy-email-pro-core/components";
import React from "react";
import he from "he";
import dayjs from "dayjs";
import { Liquid } from "liquidjs";
import { chunk, get, startCase } from "lodash";

const MERGE_TAG_PATTERN = /{{([\s\S]+?)}}/;

function isNumeric(x: string | number): x is number {
  return (typeof x === "number" || typeof x === "string") && !isNaN(Number(x));
}

export class TemplateEngine extends TemplateEnginePlugin {
  public generateIterationTemplate(
    option: LogicIteration,
    content: React.ReactNode
  ): React.ReactNode {
    return (
      <>
        <Raw>
          {`
          <!-- htmlmin:ignore -->
          {% for ${option.itemName} in ${option.dataSource} ${
            option.limit ? `limit:${option.limit}` : ""
          } %}
          <!-- htmlmin:ignore -->
          `}
        </Raw>
        {content}
        <Raw>
          {" <!-- htmlmin:ignore -->{% endfor %}  <!-- htmlmin:ignore -->"}
        </Raw>
      </>
    );
  }

  public generateConditionTemplate(
    option: LogicCondition | string,
    content: React.ReactNode,
    fallback?: React.ReactNode
  ): React.ReactNode {
    if (typeof option === "string") {
      return (
        <>
          <Raw>
            {`
            <!-- htmlmin:ignore -->{% if ${option} %}<!-- htmlmin:ignore -->
            `}
          </Raw>
          {content}
          <Raw>
            {"<!-- htmlmin:ignore -->{% else %}<!-- htmlmin:ignore -->"}
          </Raw>
          {fallback}
          <Raw>
            {"<!-- htmlmin:ignore -->{% endif %} <!-- htmlmin:ignore -->"}
          </Raw>
        </>
      );
    }

    const { symbol, groups } = option;

    const generateExpression = (condition: {
      left: string | number;
      operator: ConditionOperator;
      right: string | number;
    }) => {
      if (condition.operator === ConditionOperator.TRUTHY) {
        return condition.left;
      }
      if (condition.operator === ConditionOperator.FALSY) {
        return condition.left + " == nil" || condition.left + " == false";
      }
      if (condition.operator === ConditionOperator.EMPTY) {
        return condition.left + " == empty";
      }
      if (condition.operator === ConditionOperator.NOT_EMPTY) {
        return condition.left + " not empty";
      }
      return (
        condition.left +
        " " +
        condition.operator +
        " " +
        (isNumeric(condition.right)
          ? condition.right
          : `"${he.encode(condition.right)}"`)
      );
    };
    const uuid = +new Date();
    const variables = groups.map((_, index) => `con_${index}_${uuid}`);

    const assignExpression = groups
      .map((item, index) => {
        return `{% assign ${variables[index]} = ${item.groups
          .map(generateExpression)
          .join(` ${item.symbol} `)} %}`;
      })
      .join("\n");
    const conditionExpression = variables.join(` ${symbol} `);

    return (
      <>
        <Raw>
          {`
          <!-- htmlmin:ignore -->
          ${assignExpression}
          {% if ${conditionExpression} %}
          <!-- htmlmin:ignore -->
          `}
        </Raw>
        {content}
        <Raw>
          {`
          <!-- htmlmin:ignore -->
          {% endif %}
          <!-- htmlmin:ignore -->
          `}
        </Raw>
      </>
    );
  }

  public generateVariable(variable: string, defaultValue?: string): string {
    if (defaultValue) {
      return `{{ ${variable}| default: "${he.encode(defaultValue)}" }}`;
    }
    return `{{ ${variable} }}`;
  }

  public isVariable(value: string): boolean {
    return MERGE_TAG_PATTERN.test(value);
  }

  public renderWithData(html: string, data: Record<string, any>): string {
    const engine = new Liquid();

    engine.plugin(function () {
      this.registerFilter("title_case", (name: string) => {
        return startCase(name);
      });

      this.registerFilter("chunk", (x, num = 1) => chunk(x, num));
      this.registerFilter(
        "arr_filter",
        (arr: Array<Record<string, any>>, key, ...ids: string[]) => {
          if (!arr) return null;
          return arr.filter((item) => ids.includes(item[key]));
        }
      );
      this.registerFilter(
        "arr_filter_sort",
        (arr: Array<Record<string, any>>, key, ...ids: string[]) => {
          if (!arr) return null;
          if (!ids) return null;
          return ids
            .map((id) => arr.find((item) => get(item, key) === id))
            .filter(Boolean);
        }
      );
      this.registerFilter(
        "arr_slice",
        (arr: Array<Record<string, any>>, start: number, end: number) => {
          if (!arr) return null;
          return arr.slice(start, end);
        }
      );
      this.registerFilter("date", (date: string) => {
        return dayjs(date).format("MMM DD, YY");
      });
      this.registerFilter("money", (amount: number) => {
        const format = get(data, `shop.money_in_emails_format`, "");
        if (!format) return amount;
        return this.parseAndRenderSync(format, {
          amount: Number(amount).toFixed(2),
        });
      });
      this.registerFilter("money_with_currency", (amount: number) => {
        const format = get(
          data,
          `shop.money_with_currency_in_emails_format`,
          ""
        );
        if (!format) return amount;
        return this.parseAndRenderSync(format, {
          amount: Number(amount).toFixed(2),
        });
      });
      this.registerFilter("money_without_currency", (amount: number) => {
        return Number(amount).toFixed(2);
      });

      this.registerFilter("money_without_trailing_zeros", (amount: number) => {
        const format = get(data, `shop.money_in_emails_format`, "");
        if (!format) return amount;
        return this.parseAndRenderSync(format, { amount: Number(amount) });
      });

      this.registerFilter("img_url", (item: string) => {
        return get(item, "image") || get(item, "src") || "";
      });
      this.registerFilter("format_address", (str: string) => {
        return str;
      });
      this.registerFilter("payment_type_img_url", (item: string) => {
        return item;
      });
    });
    const tpl = engine.parse(html);
    return engine.renderSync(tpl, data);
  }
}
